import { linkDocumentToEvent } from './link-document-to-event';
import { invalidateSurfaceCalendarEvent } from './use-surface-calendar-event';
import { invalidateEntityAttendees } from '@/lib/recordings/use-entity-attendees';

/**
 * Linking and unlinking a meeting, with the cache drops that make the change
 * visible.
 *
 * The invalidations are the load-bearing part. Two caches sit downstream of a
 * link and neither is the calendar store the mutation writes: the surface's
 * own event cache, keyed by (entity, surface), and the attendee cache, which
 * the server resolves from the linked event. Miss either and the mutation
 * looks inert rather than failing — the row keeps rendering its pre-change
 * answer.
 *
 * Both awaits are deliberate. Invalidating before the server commits lets a
 * mounted hook refetch the answer being replaced and cache it a second time.
 */

interface LinkArgs {
  eventId: string;
  entityId: string;
  surfaceId: string | null;
  linkDocument: Parameters<typeof linkDocumentToEvent>[0]['linkDocument'];
}

export async function linkEventToSurface({
  eventId,
  entityId,
  surfaceId,
  linkDocument,
}: LinkArgs): Promise<void> {
  await linkDocumentToEvent({
    eventId,
    documentId: entityId,
    canvasDocumentId: null,
    surfaceId,
    linkDocument,
  });
  // Guarded rather than assumed non-null: the pill this replaces only type-
  // checked because an early return had already narrowed it, and a caller
  // without that guard would otherwise cache-bust the key "doc:null".
  if (surfaceId) invalidateSurfaceCalendarEvent(entityId, surfaceId);
  // Unscoped: a fresh link can change what any of the entity's attendee
  // consumers resolve, and the roster is served per entity.
  invalidateEntityAttendees(entityId);
}

interface UnlinkArgs {
  eventId: string;
  entityId: string;
  surfaceId: string | null;
  unlinkDocument: (eventId: string, entityId: string, surfaceId: string | null) => Promise<void>;
}

export async function unlinkEventFromSurface({
  eventId,
  entityId,
  surfaceId,
  unlinkDocument,
}: UnlinkArgs): Promise<void> {
  await unlinkDocument(eventId, entityId, surfaceId);
  // Guarded, unlike the link path: with no surface there is no per-surface
  // cache entry to drop.
  if (surfaceId) invalidateSurfaceCalendarEvent(entityId, surfaceId);
  // Scoped, unlike the link path: the roster that just changed is this
  // surface's, and a document's other surfaces keep their own.
  invalidateEntityAttendees(entityId, surfaceId);
}
